import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import {
  generateEmergencyCode,
  getEmergencyStatus,
  revokeEmergencyCode,
  getEmergencyAuditLog,
} from '../services/emergencyService';
import type {
  GenerateEmergencyCodeResponse,
  EmergencyTokenStatus,
  EmergencyAuditEntry,
  EmergencyCategory,
} from '../types/emergency';
import {
  ALL_EMERGENCY_CATEGORIES,
  RECOMMENDED_CATEGORIES,
} from '../types/emergency';

const EmergencyDashboard: React.FC = () => {
  const { user } = useAuth();

  const [emergencyStatus, setEmergencyStatus] = useState<EmergencyTokenStatus | null>(null);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState('');
  const [generatedCode, setGeneratedCode] = useState<GenerateEmergencyCodeResponse | null>(null);
  const [auditLog, setAuditLog] = useState<EmergencyAuditEntry[]>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<EmergencyCategory[]>(
    ALL_EMERGENCY_CATEGORIES.map(c => c.key)
  );
  const walletCardRef = useRef<HTMLDivElement>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const status = await getEmergencyStatus();
      setEmergencyStatus(status);
      if (status.token?.sharedCategories && status.token.sharedCategories.length > 0) {
        setSelectedCategories(status.token.sharedCategories);
      }
    } catch (err: any) {
      console.warn('Emergency status check failed:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const toggleCategory = (key: EmergencyCategory) => {
    setSelectedCategories(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key)
        : [...prev, key]
    );
  };

  const deselectedRecommended = RECOMMENDED_CATEGORIES.filter(
    c => !selectedCategories.includes(c)
  );

  const handleGenerate = async () => {
    setEmergencyLoading(true);
    setEmergencyError('');
    setGeneratedCode(null);
    try {
      const result = await generateEmergencyCode(selectedCategories);
      setGeneratedCode(result);
      await fetchStatus();
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to generate emergency code');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Are you sure you want to revoke your emergency access code? You will need to generate a new one.')) return;
    setEmergencyLoading(true);
    setEmergencyError('');
    try {
      await revokeEmergencyCode();
      setGeneratedCode(null);
      await fetchStatus();
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to revoke emergency code');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleViewAudit = async () => {
    if (showAuditLog) {
      setShowAuditLog(false);
      return;
    }
    setAuditLoading(true);
    try {
      const result = await getEmergencyAuditLog();
      setAuditLog(result.entries);
      setShowAuditLog(true);
    } catch (err: any) {
      setEmergencyError(err.message || 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDownloadWalletCard = () => {
    const svg = walletCardRef.current?.querySelector('svg');
    const qrSvgHtml = svg ? svg.outerHTML : '';
    const accessId = generatedCode?.accessId || emergencyStatus?.token?.accessId || '';
    const pin = generatedCode?.pin || '';
    const patientName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    const expires = formatDate(generatedCode?.expiresAt || emergencyStatus?.token?.expiresAt || '');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head><title>SilverScribe Emergency Card</title>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=swap" rel="stylesheet">
      <style>
        body { margin: 0; padding: 30px; font-family: 'Nunito Sans', Arial, sans-serif; background: #fff; display: flex; flex-direction: column; align-items: center; gap: 24px; }
        .cut-label { font-size: 10px; color: #aaa; text-align: center; margin-bottom: 2px; letter-spacing: 0.5px; }
        .card { width: 3.375in; height: 2.125in; border-radius: 14px; padding: 16px 22px; box-sizing: border-box; position: relative; overflow: hidden; border: 2px dashed #ccc; }
        .card-front { background: linear-gradient(145deg, rgb(80,0,108), rgb(55,0,75)); color: rgb(245,232,218); }
        .card-front::before { content: ''; position: absolute; top: -20px; right: -20px; width: 100px; height: 100px; border-radius: 50%; background: rgba(206,140,165,.15); }
        .card-back { background: linear-gradient(145deg, rgb(110,20,145), rgb(80,0,108)); color: rgb(245,232,218); display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .card-type { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: rgb(206,140,165); margin-bottom: 2px; }
        .card-name { font-family: 'DM Serif Display', Georgia, serif; font-size: 24px; margin-bottom: 0; }
        .card-body { display: flex; justify-content: space-between; align-items: flex-end; }
        .card-fields { display: flex; flex-direction: column; gap: 4px; padding-bottom: 14px; }
        .card-field-row { display: flex; align-items: baseline; gap: 8px; }
        .card-fl { color: rgb(206,140,165); font-size: 10px; text-transform: uppercase; letter-spacing: 1px; min-width: 62px; }
        .card-fv { font-size: 14px; font-weight: 700; font-family: monospace; letter-spacing: 1px; }
        .card-qr { width: 90px; height: 90px; background: rgb(245,232,218); border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .card-qr svg { width: 74px; height: 74px; }
        .card-instruction { font-size: 9.5px; color: rgba(245,232,218,.85); letter-spacing: 0.3px; line-height: 1.4; margin: 10px 0 1px; }
        .back-title { font-family: 'DM Serif Display', Georgia, serif; font-size: 24px; margin-top: 10px; margin-bottom: 12px; }
        .back-desc { font-size: 11px; line-height: 1.5; color: rgba(245,232,218,.8); max-width: 260px; margin-bottom: 12px; }
        .back-url { font-size: 10px; color: rgb(206,140,165); letter-spacing: 1px; }
        .print-instructions { width: 3.375in; margin-top: 8px; padding: 16px 20px; border: 1px solid #E4DFE8; border-radius: 10px; background: rgba(80,0,108,.04); box-sizing: border-box; }
        .print-instructions h3 { font-size: 13px; color: rgb(80,0,108); margin: 0 0 6px; display: flex; align-items: center; gap: 6px; }
        .print-instructions p { font-size: 11px; color: #555; line-height: 1.5; margin: 0; }
        @media print { body { padding: 10px; } }
      </style>
      </head>
      <body>
        <div class="cut-label">&#9986; Cut along dotted line</div>
        <div class="card card-front">
          <div class="card-type">Medical Emergency Card</div>
          <div class="card-name">${patientName}</div>
          <div class="card-instruction">Medical Provider: Scan QR code or<br>visit <strong>silverscribe.ca/emergency</strong></div>
          <div class="card-body">
            <div class="card-fields">
              <div class="card-field-row">
                <div class="card-fl">Access ID</div>
                <div class="card-fv">${accessId}</div>
              </div>
              ${pin ? `<div class="card-field-row"><div class="card-fl">PIN</div><div class="card-fv">${pin}</div></div>` : ''}
              <div class="card-field-row">
                <div class="card-fl">Expires</div>
                <div class="card-fv" style="font-size:12px;letter-spacing:0">${expires}</div>
              </div>
            </div>
            <div class="card-qr">${qrSvgHtml}</div>
          </div>
        </div>
        <div class="cut-label">&#9986; Cut along dotted line</div>
        <div class="card card-back">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgb(206,140,165)" stroke-width="1.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
          </svg>
          <div class="back-title">SilverScribe</div>
          <div class="back-desc">This card provides one-time emergency access to de-identified health information. Scan the QR code or visit silverscribe.ca/emergency and enter the Access ID + PIN.</div>
          <div class="back-url">silverscribe.ca</div>
        </div>
        <div class="print-instructions">
          <h3>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgb(80,0,108)" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Printing Instructions
          </h3>
          <p>Print on regular paper or card stock, then cut along the dotted lines. We recommend laminating it for your wallet.</p>
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'generated': return 'Code Generated';
      case 'accessed': return 'Emergency Access';
      case 'revoked': return 'Code Revoked';
      case 'expired': return 'Code Expired';
      case 'failed_attempt': return 'Failed Attempt';
      default: return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'generated': return 'var(--green)';
      case 'accessed': return 'var(--amber)';
      case 'revoked': return 'var(--red)';
      case 'expired': return 'var(--text-light)';
      case 'failed_attempt': return 'var(--red)';
      default: return 'var(--text)';
    }
  };

  const hasCode = generatedCode || (emergencyStatus?.hasActiveToken && emergencyStatus?.token);

  return (
    <div className="emergency-dashboard">
      {emergencyError && (
        <div className="content-card" style={{
          background: 'var(--red-bg)',
          border: '1px solid rgba(192, 57, 43, .3)',
          color: 'var(--red)',
        }}>
          {emergencyError}
        </div>
      )}

      {/* ─── Purple Header Banner ─── */}
      <div className="content-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '24px' }}>
        <div style={{
          background: 'linear-gradient(135deg, rgb(80, 0, 108), rgb(110, 20, 145))',
          padding: '24px 32px',
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '24px', color: 'var(--cream, #f5e8da)' }}>
            My Emergency Card
          </div>
          <div style={{ fontSize: '15px', color: 'var(--pink, rgb(206,140,165))', marginTop: '4px' }}>
            Create a secure card that gives others access to your critical health info in an emergency.
          </div>
        </div>
      </div>

      {/* ─── Step 1: Choose What to Share ─── */}
      <div className="ec-step content-card">
        <div className="ec-step-header">
          <div className="ec-step-num">1</div>
          <div className="ec-step-title">Choose What to Share</div>
        </div>
        <div className="ec-step-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {ALL_EMERGENCY_CATEGORIES.map(({ key, label }) => (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(key)}
                  onChange={() => toggleCategory(key)}
                  style={{
                    width: '20px',
                    height: '20px',
                    accentColor: 'var(--primary)',
                    cursor: 'pointer',
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {deselectedRecommended.length > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'var(--amber-bg)',
              border: '1px solid rgba(184, 134, 11, .3)',
              borderRadius: '8px',
              color: 'var(--amber)',
              fontSize: '0.85rem',
              lineHeight: 1.5,
            }}>
              <strong>Recommended:</strong> {deselectedRecommended.map(c =>
                ALL_EMERGENCY_CATEGORIES.find(cat => cat.key === c)?.label
              ).join(', ')} {deselectedRecommended.length === 1 ? 'is' : 'are'} important for emergency care. Consider keeping {deselectedRecommended.length === 1 ? 'it' : 'them'} enabled.
            </div>
          )}
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-light)', fontStyle: 'italic' }}>
            Demographics (age range, gender, and blood type if available) are always included. Personally identifiable information such as your full name, date of birth, and address is never shared.
          </div>
        </div>
      </div>

      {/* ─── Step 2: Generate Access Code ─── */}
      <div className="ec-step content-card">
        <div className="ec-step-header">
          <div className="ec-step-num">2</div>
          <div className="ec-step-title">Generate Access Code</div>
        </div>
        <div className="ec-step-body">
          <div style={{
            padding: '14px 18px',
            background: 'rgba(212, 168, 83, .08)',
            borderRadius: '10px',
            marginBottom: '16px',
            fontSize: '14px',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}>
            <strong>Note:</strong> Only one active code per account. Generating a new code invalidates the previous one. Codes expire after 6 months.
          </div>

          <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '14px', fontStyle: 'italic' }}>
            By generating this code, you consent to sharing the selected health information with anyone who enters it.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {(!emergencyStatus?.hasActiveToken || !emergencyStatus?.token) && (
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={emergencyLoading}
                style={{ padding: '14px 28px', fontSize: '15px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                {emergencyLoading ? 'Generating...' : 'Generate Code'}
              </button>
            )}

            {emergencyStatus?.hasActiveToken && (
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={emergencyLoading}
                style={{ padding: '14px 28px', fontSize: '15px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                {emergencyLoading ? 'Generating...' : 'Regenerate Code'}
              </button>
            )}

            <button
              className="btn btn-secondary"
              onClick={handleViewAudit}
              disabled={auditLoading}
              style={{ fontSize: '13px' }}
            >
              {auditLoading ? 'Loading...' : showAuditLog ? 'Hide Access Log' : 'View Access Log'}
              {!showAuditLog && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </button>
          </div>

          {/* Success banner */}
          {generatedCode && (
            <div style={{
              marginTop: '18px',
              padding: '14px 18px',
              background: 'var(--green-bg)',
              border: '1px solid rgba(40, 167, 69, .3)',
              borderRadius: '10px',
            }}>
              <p style={{ color: 'var(--green)', fontWeight: 700, marginBottom: '0.25rem', fontSize: '0.9rem', margin: 0 }}>
                &#10003; Emergency code generated successfully!
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
                Save your PIN — it will not be shown again. Print or save the card below.
              </p>
            </div>
          )}

          {/* Active code inline result */}
          {emergencyStatus?.hasActiveToken && emergencyStatus.token && (
            <div style={{
              marginTop: '18px',
              padding: '18px 22px',
              background: 'var(--white, #fff)',
              border: '2px solid var(--primary)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: '22px', fontWeight: 800, color: 'var(--primary)', letterSpacing: '2px' }}>
                  {emergencyStatus.token.accessId}
                </div>
                {generatedCode?.pin && (
                  <div style={{ fontSize: '14px', color: 'var(--text)', marginTop: '4px' }}>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>PIN: </span>
                    <span style={{ fontFamily: "'Courier New', monospace", fontWeight: 800, letterSpacing: '2px' }}>{generatedCode.pin}</span>
                  </div>
                )}
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Generated: {formatDate(emergencyStatus.token.createdAt || emergencyStatus.token.expiresAt)} &middot; Expires: {formatDate(emergencyStatus.token.expiresAt)} &middot; <span style={{ color: 'var(--primary)', fontWeight: 700 }}>Active</span>
                </div>
                {emergencyStatus.token.sharedCategories && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '8px' }}>
                    {ALL_EMERGENCY_CATEGORIES.map(({ key, label }) => {
                      const isShared = emergencyStatus.token!.sharedCategories!.includes(key);
                      return (
                        <span
                          key={key}
                          style={{
                            padding: '0.2rem 0.6rem',
                            borderRadius: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            background: isShared ? 'var(--green-bg)' : 'var(--bg)',
                            color: isShared ? 'var(--green)' : 'var(--text-light)',
                            border: `1px solid ${isShared ? 'rgba(40, 167, 69, .3)' : 'var(--border)'}`,
                          }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleRevoke}
                disabled={emergencyLoading}
              >
                Revoke
              </button>
            </div>
          )}

          {/* Used/revoked code status */}
          {emergencyStatus && !emergencyStatus.hasActiveToken && emergencyStatus.token && (
            <div style={{
              marginTop: '18px',
              padding: '14px 18px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: '1rem', color: 'var(--text-light)' }}>
                  {emergencyStatus.token.accessId}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Status: <span style={{
                    color: emergencyStatus.token.status === 'used' ? 'var(--amber)' : 'var(--red)',
                    fontWeight: 700, textTransform: 'capitalize',
                  }}>{emergencyStatus.token.status}</span>
                  {emergencyStatus.token.usedAt && (
                    <> &middot; Used: {formatDate(emergencyStatus.token.usedAt)}</>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Audit Log */}
          {showAuditLog && (
            <div style={{ marginTop: '18px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700, color: 'var(--text)', margin: '0 0 12px' }}>
                Access Log
              </h3>
              {auditLog.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="ed-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((entry, i) => (
                        <tr key={i}>
                          <td>{formatDate(entry.timestamp)}</td>
                          <td>
                            <span style={{ color: getActionColor(entry.action), fontWeight: 700 }}>
                              {getActionLabel(entry.action)}
                            </span>
                          </td>
                          <td style={{ textTransform: 'uppercase' }}>{entry.method || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty-state">No access history yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Step 3: Print Your Emergency Card ─── */}
      <div className="ec-step content-card">
        <div className="ec-step-header">
          <div className="ec-step-num">3</div>
          <div className="ec-step-title">Print Your Emergency Card</div>
        </div>
        <div className="ec-step-body">
          {!hasCode ? (
            /* Empty state */
            <div style={{ maxWidth: '420px', margin: '0 auto' }}>
              <div className="ec-empty">
                <div className="ec-empty-inner">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(206,140,165,.5)" strokeWidth="1.5" style={{ marginBottom: '12px' }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-light)' }}>
                    Your Emergency Card will appear here
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-light)', marginTop: '6px' }}>
                    Complete Steps 1 and 2 to generate your card.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Filled state */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '18px', textAlign: 'center' }}>
                Print the card below, cut along the dotted border, and keep it in your wallet. We recommend laminating for durability.
              </div>

              {/* Card front */}
              <div style={{ maxWidth: '420px', width: '100%' }}>
                <div className="ec-card-wrapper">
                  <div className="ec-card ec-card-front" ref={walletCardRef}>
                    <div className="ec-card-circle" />
                    <div className="ec-card-type">Medical Emergency Card</div>
                    <div className="ec-card-name">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="ec-card-instruction">
                      Medical Provider: Scan QR code or<br />visit <strong>silverscribe.ca/emergency</strong>
                    </div>
                    <div className="ec-card-body">
                      <div className="ec-card-fields">
                        <div className="ec-card-field-row">
                          <div className="ec-card-field-label">Access ID</div>
                          <div className="ec-card-field-value">
                            {generatedCode?.accessId || emergencyStatus?.token?.accessId}
                          </div>
                        </div>
                        {generatedCode?.pin && (
                          <div className="ec-card-field-row">
                            <div className="ec-card-field-label">PIN</div>
                            <div className="ec-card-field-value">{generatedCode.pin}</div>
                          </div>
                        )}
                        <div className="ec-card-field-row">
                          <div className="ec-card-field-label">Expires</div>
                          <div className="ec-card-field-value" style={{ fontSize: 12, letterSpacing: 0 }}>
                            {formatDate(generatedCode?.expiresAt || emergencyStatus?.token?.expiresAt || '')}
                          </div>
                        </div>
                      </div>
                      {generatedCode?.pin && (
                        <div className="ec-card-qr">
                          <QRCodeSVG
                            value={generatedCode?.qrUrl || `https://www.silverscribe.ca/emergency?id=${emergencyStatus?.token?.accessId || ''}`}
                            size={74}
                            bgColor="transparent"
                            fgColor="rgb(80, 0, 108)"
                            level="M"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card back */}
                <div className="ec-card-wrapper" style={{ marginTop: '20px' }}>
                  <div className="ec-card ec-card-back">
                    <svg className="ec-back-logo" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgb(206,140,165)" strokeWidth="1.5">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                    <div className="ec-back-title">SilverScribe</div>
                    <div className="ec-back-desc">
                      This card provides one-time emergency access to de-identified health information.
                      Scan the QR code or visit silverscribe.ca/emergency and enter the Access ID + PIN.
                    </div>
                    <div className="ec-back-url">silverscribe.ca</div>
                  </div>
                </div>
              </div>

              {/* PIN note */}
              {generatedCode?.pin ? (
                <div className="ec-pin-note" style={{ marginTop: '20px' }}>
                  <strong>Note:</strong> Your PIN is only visible during this session. If lost, you will need to regenerate your emergency code and reprint the card.
                </div>
              ) : (
                <div className="ec-pin-note" style={{ marginTop: '20px', background: 'var(--red-bg)', borderColor: 'rgba(192, 57, 43, .3)', color: 'var(--red)' }}>
                  <strong>PIN no longer available.</strong> To print or save your card, regenerate your emergency code from Step 2 above.
                </div>
              )}

              {/* Card action buttons */}
              <div className="ec-card-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={generatedCode?.pin ? handleDownloadWalletCard : () => window.alert('Your PIN is no longer available. Please regenerate your emergency code to print a new card.')}
                  disabled={!generatedCode?.pin}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 6 2 18 2 18 9"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print Card
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={generatedCode?.pin ? handleDownloadWalletCard : () => window.alert('Your PIN is no longer available. Please regenerate your emergency code to save a new card.')}
                  disabled={!generatedCode?.pin}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Save as PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ec-step {
          padding: 24px;
          margin-bottom: 20px;
        }
        .ec-step-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .ec-step-num {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--primary);
          color: var(--cream, #f5e8da);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 13px;
          flex-shrink: 0;
        }
        .ec-step-title {
          font-weight: 800;
          font-size: 18px;
          color: var(--text);
          font-family: var(--font-display);
        }
        .ec-step-body {
          margin-left: 38px;
        }
        .ec-empty {
          border: 2px dashed var(--border);
          border-radius: 12px;
          padding: 4px;
        }
        .ec-empty-inner {
          background: linear-gradient(135deg, rgba(80, 0, 108, .3), rgba(110, 20, 145, .3));
          border-radius: 10px;
          padding: 40px 22px;
          text-align: center;
        }
        .ec-card-wrapper {
          border: 2px dashed var(--text-muted);
          border-radius: 12px;
          padding: 4px;
        }
        .ed-label {
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: block;
        }
        .ed-table {
          width: 100%;
          border-collapse: collapse;
        }
        .ed-table th {
          text-align: left;
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 12px 16px;
          border-bottom: 2px solid var(--border);
          background: var(--bg);
        }
        .ed-table td {
          color: var(--text);
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          font-size: 0.85rem;
        }
        .ed-table tbody tr:last-child td {
          border-bottom: none;
        }
        .ed-table tbody tr:hover {
          background: var(--primary-bg);
        }
        .ec-card {
          width: 100%;
          aspect-ratio: 3.375 / 2.125;
          border-radius: 10px;
          padding: 16px 22px;
          position: relative;
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          box-sizing: border-box;
        }
        .ec-card-front {
          background: linear-gradient(145deg, rgb(80, 0, 108), rgb(55, 0, 75));
          color: var(--cream);
        }
        .ec-card-circle {
          position: absolute;
          top: -20px;
          right: -20px;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: rgba(206, 140, 165, .15);
        }
        .ec-card-type {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: var(--pink);
          margin-bottom: 2px;
        }
        .ec-card-name {
          font-family: var(--font-display);
          font-size: 24px;
          margin-bottom: 0;
        }
        .ec-card-body {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .ec-card-fields {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding-bottom: 14px;
        }
        .ec-card-field-row {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }
        .ec-card-field-label {
          color: var(--pink);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 1px;
          min-width: 62px;
        }
        .ec-card-field-value {
          font-size: 14px;
          font-weight: 700;
          font-family: monospace;
          letter-spacing: 1px;
        }
        .ec-card-instruction {
          font-size: 9.5px;
          color: var(--cream, rgba(245, 232, 218, .85));
          letter-spacing: 0.3px;
          line-height: 1.4;
          margin: 10px 0 1px;
        }
        .ec-card-qr {
          width: 90px;
          height: 90px;
          background: var(--cream);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .ec-card-back {
          background: linear-gradient(145deg, rgb(110, 20, 145), rgb(80, 0, 108));
          color: var(--cream);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        .ec-back-logo {
          margin-bottom: 8px;
        }
        .ec-back-title {
          font-family: var(--font-display);
          font-size: 24px;
          margin-bottom: 12px;
        }
        .ec-back-desc {
          font-size: 11px;
          line-height: 1.5;
          color: rgba(245, 232, 218, .8);
          max-width: 260px;
          margin-bottom: 12px;
        }
        .ec-back-url {
          font-size: 10px;
          color: var(--pink);
          letter-spacing: 1px;
        }
        .ec-pin-note {
          width: 100%;
          max-width: 420px;
          background: var(--amber-bg);
          border: 1px solid rgba(184, 134, 11, .3);
          border-radius: var(--radius-sm);
          padding: 10px 14px;
          box-sizing: border-box;
          font-size: 0.8rem;
          color: var(--amber);
          line-height: 1.5;
        }
        .ec-card-actions {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          justify-content: center;
          margin-top: 20px;
        }
        @media (max-width: 768px) {
          .ec-step-body {
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default EmergencyDashboard;
